// Detay alanının tabloları — anahtarlar, etiketler, sayfalar.
//
// Üçü de daha önce `.mini-list` satırlarıydı: 396 px'lik dar panelde tek çare
// buydu. Detay alanı sayfanın dörtte üçüne çıkınca liste biçimi hem yer israfı
// hem okunaksızdı (değer, kalite ve yaş aynı satıra sıkışıyordu). Artık üçü de
// `.grid` tablosu: kolonlar hizalı, sıralama gözle yapılabiliyor.

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '../../components/Toast'
import { useDrawer } from '../../components/Drawer'
import OnayDrawer from '../../components/OnayDrawer'
import { tr, trTarihSaat } from '../../lib/trFormat'
import { ApiHatasi } from '../../lib/api'
import { KopyaMini } from './Kopyala'
import { akisDurumu, kaliteRozeti, sonDegerMetni, yasMetni } from './bicim'
import {
  entegrasyon,
  type AnahtarSatiri,
  type EntegrasyonDurumu,
  type EtiketSatiri,
  type SayfaSatiri,
} from './api'

export function DurumRozeti({ durum }: { durum: EntegrasyonDurumu }) {
  return durum === 'aktif' ? (
    <span className="badge b-navy">
      <span className="dot" />
      Aktif
    </span>
  ) : (
    <span className="badge b-gray">Pasif</span>
  )
}

const GOZ = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

const GOZ_KAPALI = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19M1 1l22 22" />
  </svg>
)

// ------------------------------------------------------------------ anahtarlar

export function AnahtarTablosu({
  anahtarlar,
  yazabilir,
  yeniAnahtar,
}: {
  anahtarlar: AnahtarSatiri[]
  yazabilir: boolean
  yeniAnahtar: () => void
}) {
  const kuyruk = useQueryClient()
  const { ac } = useDrawer()

  /** Düz metni açılmış anahtarlar — varsayılan KAPALI, sır ekranda durmasın. */
  const [acik, setAcik] = useState<Record<string, string>>({})

  const tazele = (kaynakId: string) => {
    kuyruk.invalidateQueries({ queryKey: ['entegrasyon-kaynak', kaynakId] })
    kuyruk.invalidateQueries({ queryKey: ['entegrasyon-kaynaklar'] })
  }

  const goster = useMutation({
    mutationFn: (id: string) => entegrasyon.anahtarGoster(id),
    onSuccess: (g) => {
      if (g.duzMetin) setAcik((o) => ({ ...o, [g.id]: g.duzMetin! }))
      else toast(g.not ?? 'Bu anahtarın düz metni saklanmamış.')
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Anahtar gösterilemedi'),
  })

  const iptal = useMutation({
    mutationFn: (id: string) => entegrasyon.anahtarIptal(id),
    onSuccess: (a) => {
      tazele(a.kaynakId)
      toast(`${a.ad} · anahtar iptal edildi — bu anahtarla gelen istek 401 alır`)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Anahtar iptal edilemedi'),
  })

  const kaliciSil = useMutation({
    mutationFn: (a: AnahtarSatiri) => entegrasyon.anahtarKaliciSil(a.id).then(() => a),
    onSuccess: (a) => {
      tazele(a.kaynakId)
      toast(`${a.ad} · anahtar kaydı silindi — paket defteri etkilenmedi`)
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Anahtar silinemedi'),
  })

  return (
    <>
      <div className="toolbar">
        <div className="toolbar-right" style={{ marginRight: 'auto' }}>
          <span>
            {anahtarlar.filter((a) => a.durum === 'aktif').length} etkin ·{' '}
            {anahtarlar.filter((a) => a.durum === 'pasif').length} iptal
          </span>
        </div>
        <button
          className="btn btn-pri btn-sm"
          type="button"
          disabled={!yazabilir}
          title={yazabilir ? 'Yeni API anahtarı üret' : 'Bu modülde yazma yetkiniz yok'}
          onClick={yeniAnahtar}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Yeni anahtar
        </button>
      </div>

      <table className="grid">
        <thead>
          <tr>
            <th style={{ width: '20%' }}>Ad</th>
            <th style={{ width: 132 }}>Kapsam</th>
            <th>Anahtar</th>
            <th style={{ width: 116 }}>Üretim</th>
            <th style={{ width: 116 }}>Son Kullanım</th>
            <th style={{ width: 74 }}>Durum</th>
            <th style={{ width: 86 }} />
          </tr>
        </thead>
        <tbody>
          {anahtarlar.length === 0 && (
            <tr>
              <td colSpan={7} className="cell-sub" style={{ padding: 14 }}>
                Anahtar yok — ajan paket gönderemez, gönderdiği her paket 401 alıp
                ölü mektuba düşer.
              </td>
            </tr>
          )}

          {anahtarlar.map((a) => (
            <tr key={a.id} className={a.durum === 'pasif' ? 'storno-r' : undefined}>
              <td>
                <div className="cell-name">{a.ad}</div>
                {a.iptalZamani && (
                  <div className="cell-sub">iptal {trTarihSaat(a.iptalZamani)}</div>
                )}
              </td>
              <td>
                {/*
                  Anahtar kapsamı KAYNAKTIR: ingest yalnız anahtarın kaynağına
                  bakar, sayfayı paketin page.id'si seçer. Sayfa etiketi bu
                  yüzden bir kısıt değil, "hangi kurulum için üretildi" notu.
                */}
                {a.sayfaKodu ? (
                  <span
                    className="badge b-navy"
                    title={`${a.sayfaKodu} kurulumu için üretildi — kaynağın tüm sayfalarında geçerli`}
                  >
                    {a.sayfaKodu}
                  </span>
                ) : a.oncekiSayfaKodu ? (
                  /*
                    Sayfası SİLİNMİŞ anahtar. Burayı yalnız "Kaynağın tamamı"
                    diye göstermek kaydı tahrif ederdi: satır, doğuştan kaynak
                    kapsamlı üretilmiş bir anahtardan ayırt edilemez olurdu ve
                    sonradan bakan kişi anahtarın kasten geniş üretildiği
                    sonucuna varırdı. Bilginin başka nüshası yok — paketler
                    anahtar kimliği taşımıyor.
                  */
                  <span
                    className="badge b-amber"
                    title={`${a.oncekiSayfaKodu} sayfası için üretilmişti; o sayfa silindiğinde bağ koptu. Anahtar iptal EDİLMEDİ — çalışmaya devam ediyor.`}
                  >
                    {a.oncekiSayfaKodu} · silindi
                  </span>
                ) : (
                  <span className="cell-sub">Kaynağın tamamı</span>
                )}
              </td>
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span
                    className="mono"
                    style={{ fontSize: 11, overflowWrap: 'anywhere', flex: 1, minWidth: 0 }}
                  >
                    {acik[a.id] ?? `${a.onek}${'•'.repeat(12)}`}
                  </span>
                  <KopyaMini
                    metin={acik[a.id] ?? ''}
                    etiket="API anahtarı"
                    kapali={!acik[a.id]}
                    ipucu={acik[a.id] ? 'Anahtar panoya kopyalanır' : 'Önce anahtarı gösterin'}
                  />
                </div>
              </td>
              <td className="mono" style={{ fontSize: 11 }}>
                {trTarihSaat(a.olusturmaZamani)}
              </td>
              <td className="mono" style={{ fontSize: 11 }}>
                {a.sonKullanim ? trTarihSaat(a.sonKullanim) : '—'}
              </td>
              <td>
                <DurumRozeti durum={a.durum} />
              </td>
              <td>
                <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  {/*
                    Anahtar üretimden sonra da okunabiliyor: bu bir kullanıcı
                    şifresi değil, ajan konfigürasyonuna elle yazılan makine
                    anahtarı. Görüntüleme yazma yetkisi ister ve sunucuda
                    denetim satırına düşer.
                  */}
                  <button
                    className="mini-btn"
                    type="button"
                    disabled={!yazabilir || !a.gosterilebilir || goster.isPending}
                    title={
                      !a.gosterilebilir
                        ? 'Bu anahtar kasa eklenmeden önce üretilmiş — geri getirilemez'
                        : !yazabilir
                          ? 'Anahtarı görmek için yazma yetkisi gerekiyor'
                          : acik[a.id]
                            ? 'Gizle'
                            : 'Anahtarı göster'
                    }
                    onClick={() =>
                      acik[a.id]
                        ? setAcik(({ [a.id]: _, ...kalan }) => kalan)
                        : goster.mutate(a.id)
                    }
                  >
                    {acik[a.id] ? GOZ_KAPALI : GOZ}
                  </button>

                  <button
                    className="mini-btn"
                    type="button"
                    disabled={!yazabilir || a.durum === 'pasif' || iptal.isPending}
                    title={
                      a.durum === 'pasif'
                        ? 'Zaten iptal edilmiş — iptal geri alınmaz'
                        : yazabilir
                          ? 'Anahtarı iptal et (kayıt kalır, pasife düşer)'
                          : 'Bu modülde yazma yetkiniz yok'
                    }
                    onClick={() =>
                      // Onay ŞART: iptal anında sahaya vuruyor — o anahtarla
                      // gelen her paket 401 alıp ölü mektuba düşüyor (§2).
                      ac({
                        baslik: 'Anahtar iptal edilsin mi?',
                        altBaslik: a.ad,
                        boyut: 'sm',
                        icerik: (
                          <OnayDrawer
                            soru={`"${a.ad}" anahtarı iptal edilecek.`}
                            aciklama={
                              'Bu anahtarla gelen istekler 401 alır ve ajan o paketleri bir ' +
                              'daha göndermez — sahadaki veri kaybolur. İptal geri alınamaz; ' +
                              'ajana yeni bir anahtar tanımlamanız gerekir.'
                            }
                            onayEtiketi="İptal et"
                            tehlikeli
                            onayla={() => iptal.mutateAsync(a.id)}
                          />
                        ),
                      })
                    }
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>

                  {/*
                    Kalıcı silme YALNIZ iptal edilmiş anahtarda: etkin anahtarı
                    silmek sahadaki ajanı uyarısızca 401'e düşürür ve sözleşme
                    §2 gereği kuyruğu kaybolur. Sunucu da aynı kuralı 409 ile
                    zorluyor — buradaki gizleme sadece kolaylık.
                  */}
                  {a.durum === 'pasif' && (
                    <button
                      className="mini-btn"
                      type="button"
                      disabled={!yazabilir || kaliciSil.isPending}
                      title={
                        yazabilir
                          ? 'Kaydı tamamen sil — geri alınamaz, paket defteri etkilenmez'
                          : 'Bu modülde yazma yetkiniz yok'
                      }
                      onClick={() =>
                        ac({
                          baslik: 'Anahtar kaydı silinsin mi?',
                          altBaslik: a.ad,
                          boyut: 'sm',
                          icerik: (
                            <OnayDrawer
                              soru={`"${a.ad}" kaydı tamamen silinecek.`}
                              aciklama={
                                'Geri alınamaz. Paket defteri etkilenmez — bu anahtarla gelmiş ' +
                                'paketler yerinde kalır, yalnız anahtar satırı gider.'
                              }
                              onayEtiketi="Sil"
                              tehlikeli
                              onayla={() => kaliciSil.mutateAsync(a)}
                            />
                          ),
                        })
                      }
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6" />
                      </svg>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="grid-foot">
        <span>
          Anahtar kaynağın tamamına geçerlidir; sayfa etiketi yalnız hangi kurulum için
          üretildiğini söyler · iptal geri alınmaz, silinen anahtarla gelen paket 401 alır
          ve ajan onu bir daha göndermez
        </span>
      </div>
    </>
  )
}

// -------------------------------------------------------------------- etiketler

export function EtiketTablosu({
  etiketler,
  ac,
}: {
  etiketler: EtiketSatiri[]
  /** Satır tıklanınca etiket çekmecesini açar. */
  ac: (e: EtiketSatiri) => void
}) {
  return (
    <>
      <table className="grid">
        <thead>
          <tr>
            <th style={{ width: '26%' }}>Anahtar</th>
            <th>Ad</th>
            <th style={{ width: 74 }}>Tür</th>
            <th className="num" style={{ width: 108 }}>Son Değer</th>
            <th style={{ width: 56 }}>Birim</th>
            <th style={{ width: 92 }}>Kalite</th>
            <th style={{ width: 68 }}>Yaş</th>
            <th className="num" style={{ width: 70 }}>Ölçüm</th>
            <th style={{ width: 78 }}>Bağlantı</th>
          </tr>
        </thead>
        <tbody>
          {etiketler.length === 0 && (
            <tr>
              <td colSpan={9} className="cell-sub" style={{ padding: 14 }}>
                Henüz veri akmadı — ilk pakette etiketler kendiliğinden açılır.
              </td>
            </tr>
          )}

          {etiketler.map((e) => {
            const kalite = kaliteRozeti(e.sonKalite)
            return (
              <tr
                key={e.id}
                style={{ cursor: 'pointer' }}
                title="Adlandır · üniteye bağla · son ölçümler"
                onClick={() => ac(e)}
              >
                <td>
                  <span className="kod" style={{ overflowWrap: 'anywhere' }}>
                    {e.anahtar}
                  </span>
                </td>
                <td>
                  {e.ad ? (
                    <div className="cell-name">{e.ad}</div>
                  ) : (
                    <span className="cell-sub">adlandırılmadı</span>
                  )}
                </td>
                <td>
                  <span className={`badge ${e.tur === 'sayisal' ? 'b-navy' : 'b-gray'}`}>
                    {e.tur === 'sayisal' ? 'Sayısal' : 'Durum'}
                  </span>
                </td>
                {/* Ham metin YENİDEN biçimlendirilmiyor (§3.2) — operatör SCADA
                    ekranında ne görüyorsa burada da onu görsün. */}
                <td className="num" style={{ fontWeight: 600 }}>
                  {sonDegerMetni(e)}
                </td>
                <td className="cell-sub">{e.birim ?? '—'}</td>
                <td>
                  <span className={`badge ${kalite.sinif}`}>{kalite.etiket}</span>
                </td>
                <td className="mono" style={{ fontSize: 11 }}>
                  {yasMetni(e.sonZamanYasiSn)}
                </td>
                <td className="num">{tr(e.olcumSayisi, 0)}</td>
                <td>
                  {e.uniteId ? (
                    <span className="badge b-navy">Bağlı</span>
                  ) : (
                    <span className="cell-sub">—</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <div className="grid-foot">
        <span>
          Satıra tıklayın: adlandırma · birim · üniteye ve beyan parametresine bağlama ·
          son ölçümler
        </span>
      </div>
    </>
  )
}

// ---------------------------------------------------------------------- sayfalar

export function SayfaTablosu({
  sayfalar,
  sec,
  sayfaEkle,
  yazabilir,
}: {
  sayfalar: SayfaSatiri[]
  sec: (id: string) => void
  sayfaEkle: () => void
  yazabilir: boolean
}) {
  return (
    <>
      <div className="toolbar">
        <div className="toolbar-right" style={{ marginRight: 'auto' }}>
          <span>{sayfalar.length} sayfa</span>
        </div>
        <button
          className="btn btn-sec btn-sm"
          type="button"
          disabled={!yazabilir}
          title={yazabilir ? 'Sayfayı elle tanımla' : 'Bu modülde yazma yetkiniz yok'}
          onClick={sayfaEkle}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Sayfa ekle
        </button>
      </div>

      <table className="grid">
        <thead>
          <tr>
            <th style={{ width: 150 }}>page.id</th>
            <th>Ad</th>
            <th className="num" style={{ width: 74 }}>Etiket</th>
            <th className="num" style={{ width: 74 }}>Paket</th>
            <th style={{ width: 130 }}>Son Görülme</th>
            <th style={{ width: 84 }}>Akış</th>
            <th style={{ width: 74 }}>Durum</th>
          </tr>
        </thead>
        <tbody>
          {sayfalar.length === 0 && (
            <tr>
              <td colSpan={7} className="cell-sub" style={{ padding: 14 }}>
                Henüz sayfa tanınmadı — ilk pakette kendiliğinden açılır.
              </td>
            </tr>
          )}

          {sayfalar.map((s) => {
            const akis = akisDurumu(s.sonGorulmeYasiSn)
            return (
              <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => sec(s.id)}>
                <td>
                  <span className="kod" style={{ overflowWrap: 'anywhere' }}>
                    {s.sayfaKodu}
                  </span>
                </td>
                <td>
                  <div className="cell-name">
                    {s.ad}
                    {s.otomatikMi && (
                      <span className="badge b-amber" title="İlk pakette kendiliğinden açıldı">
                        Otomatik
                      </span>
                    )}
                  </div>
                  {s.aciklama && <div className="cell-sub">{s.aciklama}</div>}
                </td>
                <td className="num">{tr(s.etiketSayisi, 0)}</td>
                <td className="num">{tr(s.paketSayisi, 0)}</td>
                <td className="mono" style={{ fontSize: 11 }}>
                  {s.sonGorulme ? trTarihSaat(s.sonGorulme) : '—'}
                </td>
                <td>
                  <span className={`badge ${akis.sinif}`} title={akis.baslik}>
                    {akis.etiket}
                  </span>
                </td>
                <td>
                  <DurumRozeti durum={s.durum} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      <div className="grid-foot">
        <span>
          Satıra tıklayın: sayfanın kendi uç noktası, anahtarları ve etiket tablosu
        </span>
      </div>
    </>
  )
}
